import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectsGrid from '../components/ProjectsGrid';
import CreateProjectButton from '../components/CreateProjectButton';
import '../App.css';
import { t } from '../i18n';

// Utility to get API base URL depending on environment
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  // Use relative URL for production
  return '';
};

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [objects, setObjects] = useState([]); // State for the list of objects
  const [selectedObject, setSelectedObject] = useState(''); // State for the selected filter
  const [sortBy, setSortBy] = useState('name'); // 'name', 'likes', or 'difficulty'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [categories, setCategories] = useState([]); // State for the list of categories
  const [selectedCategory, setSelectedCategory] = useState(''); // State for the selected category filter
    useEffect(() => {
      // Fetch the list of categories for filtering
      fetch(`${getApiBaseUrl()}/api/categories`)
        .then((response) => response.json())
        .then((data) => setCategories(Array.isArray(data) ? data : (data.categories || [])))
        .catch((error) => console.error('Error fetching categories:', error));
    }, []);
  const navigate = useNavigate(); // Initialize useNavigate
  const [likedProjectIds, setLikedProjectIds] = useState([]); // State for the list of liked projects

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/projects`)
      .then((response) => response.json())
      .then((data) => {
        const projectsWithObjects = data.map((project) => ({
          ...project,
          objects: project.objects || [], // Ensure objects is always an array
        }));
        setProjects(projectsWithObjects);
      })
      .catch((error) => console.error('Error fetching projects:', error));
  }, []);

  useEffect(() => {
    // Fetch the list of objects for filtering
    fetch(`${getApiBaseUrl()}/api/objects`)
      .then((response) => response.json())
      .then((data) => setObjects(data)) // Set the list of objects
      .catch((error) => console.error('Error fetching objects:', error));
  }, []);

  // Utility to create a slug from a string
  function slugify(text) {
    return text
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase();
  }

  const handleProjectClick = (projectOrSlug) => {
    // If already a slug, use it directly
    if (typeof projectOrSlug === 'string' && !projectOrSlug.match(/^\d+(-|$)/)) {
      navigate(`/project/${projectOrSlug}`);
      return;
    }
    // If passed a project object, use its name
    if (typeof projectOrSlug === 'object' && projectOrSlug.name) {
      const slug = slugify(projectOrSlug.name);
      navigate(`/project/${slug}`);
      return;
    }
    // Otherwise, find project by id and use its slug
    const project = projects.find((p) => p.id === projectOrSlug || p.id === Number(projectOrSlug));
    if (project) {
      const slug = slugify(project.name);
      navigate(`/project/${slug}`);
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSelectedObject(value); // Update the selected filter
  };


  // Filter projects based on the selected object and category
  let filteredProjects = projects;
  if (selectedObject) {
    filteredProjects = filteredProjects.filter((project) =>
      project.objects.some((object) => object.id === parseInt(selectedObject))
    );
  }
  if (selectedCategory) {
    filteredProjects = filteredProjects.filter((project) =>
      Array.isArray(project.categories) &&
      project.categories.some((cat) => cat.id === parseInt(selectedCategory))
    );
  }

  // Sort filtered projects based on sortBy and sortDirection
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let result = 0;
    if (sortBy === 'name') {
      result = a.name.localeCompare(b.name);
    } else if (sortBy === 'likes') {
      result = (a.likes || 0) - (b.likes || 0);
    } else if (sortBy === 'difficulty') {
      result = (a.difficulty || 0) - (b.difficulty || 0);
    }
    return sortDirection === 'asc' ? result : -result;
  });

  // Handler for clicking a sort label
  const handleSortClick = (field) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // --- Liked project logic for logged-in users ---
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${getApiBaseUrl()}/api/users/liked-projects`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => response.ok ? response.json() : { projects: [] })
      .then((data) => {
        setLikedProjectIds(Array.isArray(data.projects) ? data.projects.map((p) => p.id) : []);
      })
      .catch(() => setLikedProjectIds([]));
  }, []);

  // Utility to check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  return (
    <div style={styles.page}>
      {/* Projets réalisés Section */}
      <div style={styles.projectsSection}>
        <h2 style={styles.sectionTitle}>{t('projectsTitle') || 'Toutes les opérations réussies'}</h2>
        {/* ProjectsGrid moved below sorting/filtering UI */}
        {/* Filter by Object Dropdown */}
        <div style={styles.filterContainer}>
          <label htmlFor="objectFilter" style={styles.filterLabel}>
            {t('filterByObject') || 'Filtrer par objet opéré :'}
          </label>
          <select
            id="objectFilter"
            value={selectedObject}
            onChange={handleFilterChange}
            style={styles.filterSelect}
          >
            <option value="">{t('allObjects') || 'Tous les objets'}</option>
            {objects.map((object) => (
              <option key={object.id} value={object.id}>
                {object.name}
              </option>
            ))}
          </select>
        </div>

        {/* Filter by Category Dropdown */}
        <div style={styles.filterContainer}>
          <label htmlFor="categoryFilter" style={styles.filterLabel}>
            {t('filterByCategory') || 'Filtrer par catégorie :'}
          </label>
          <select
            id="categoryFilter"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">{t('allCategories') || 'Toutes les catégories'}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sorting Labels */}
        <div style={styles.sortRow}>
          <span style={styles.sortLabel}>Trier par&nbsp;:</span>
          <span
            style={{
              ...styles.sortOption,
              ...(sortBy === 'likes' ? styles.activeSortOption : {}),
              cursor: 'pointer',
            }}
            onClick={() => handleSortClick('likes')}
          >
            Likes&nbsp;
            {sortBy === 'likes' && (
              <span style={styles.arrow}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            )}
          </span>
          <span
            style={{
              ...styles.sortOption,
              ...(sortBy === 'name' ? styles.activeSortOption : {}),
              cursor: 'pointer',
              marginLeft: '16px',
            }}
            onClick={() => handleSortClick('name')}
          >
            Nom&nbsp;
            {sortBy === 'name' && (
              <span style={styles.arrow}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            )}
          </span>
          <span
            style={{
              ...styles.sortOption,
              ...(sortBy === 'difficulty' ? styles.activeSortOption : {}),
              cursor: 'pointer',
              marginLeft: '16px',
            }}
            onClick={() => handleSortClick('difficulty')}
          >
            Difficulté&nbsp;
            {sortBy === 'difficulty' && (
              <span style={styles.arrow}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
            )}
          </span>
        </div>

        {/* Display message if no projects match the filter */}
        {sortedProjects.length === 0 ? (
          <p style={styles.noProjectsMessage}>
            {t('noProjectForObject') || 'Aucune opération trouvée pour cet objet, soyez le premier à en créer une !'}
          </p>
        ) : (
          <>
            <ProjectsGrid
              projects={[]}
              onProjectClick={handleProjectClick}
              likedProjectIds={isLoggedIn ? likedProjectIds : []}
              onLikeRequireLogin={() => {
                if (!isLoggedIn) navigate('/create-user');
              }}
              updateLikedProjectIds={(projectId) => {
                setLikedProjectIds((prev) => [...prev, projectId]);
              }}
            />
            {(() => {
              // Split projects into chunks of 6
              const chunks = [];
              for (let i = 0; i < sortedProjects.length; i += 6) {
                chunks.push(sortedProjects.slice(i, i + 6));
              }
              return chunks.map((chunk, idx) => (
                <React.Fragment key={idx}>
                  <ProjectsGrid
                    projects={chunk}
                    onProjectClick={handleProjectClick}
                    likedProjectIds={isLoggedIn ? likedProjectIds : []}
                    onLikeRequireLogin={() => {
                      if (!isLoggedIn) navigate('/create-user');
                    }}
                    updateLikedProjectIds={(projectId) => {
                      setLikedProjectIds((prev) => [...prev, projectId]);
                    }}
                  />
                  {/* Insert button after every 6 projects except after the last chunk */}
                  {idx < chunks.length - 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '32px 0' }}>
                      <button
                        style={{
                          padding: '12px 28px',
                          fontSize: '18px',
                          backgroundColor: 'var(--green-eucalyptus, #2ecc40)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(46,204,64,0.08)'
                        }}
                        onClick={() => navigate('/create-project')}
                      >
                        Partager ma création
                      </button>
                    </div>
                  )}
                </React.Fragment>
              ));
            })()}
          </>
        )}
      </div>

      {/* Use the CreateProjectButton component */}
      <CreateProjectButton />
    </div>
  );
};

const styles = {
  page: { position: 'relative', textAlign: 'center' },
  projectsSection: {
    margin: '0 auto', // Center the container horizontally
    padding: '0 20px', // Add padding for better spacing on smaller screens
    maxWidth: '1500px', // Limit the width to 1500px
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '20px',
    fontFamily: `'Poppins', sans-serif`,
  },
  filterContainer: {
    marginBottom: '20px',
    textAlign: 'center',
  },
  filterLabel: {
    marginRight: '10px',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  filterSelect: {
    padding: '5px 10px',
    fontSize: '16px',
    border: '1px solid var(--gray-anthracite)',
    borderRadius: '5px',
    outline: 'none',
  },
  noProjectsMessage: {
    fontSize: '18px',
    color: 'var(--gray-anthracite)',
    marginTop: '20px',
  },
  createProjectContainer: {
    marginTop: '5px',
    textAlign: 'center',
  },
  createProjectButton: {
    padding: '10px 20px',
    fontSize: '16px',
    backgroundColor: 'var(--green-eucalyptus)',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  // Sorting styles (copied from HomePage.js for consistency)
  sortRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    marginTop: 18,
    gap: 10,
    minHeight: 40,
    width: '100%',
  },
  sortLabel: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  sortOption: {
    fontSize: 16,
    padding: '4px 10px',
    borderRadius: 5,
    border: '1px solid transparent',
    transition: 'background 0.2s, border 0.2s',
    userSelect: 'none',
  },
  activeSortOption: {
    background: '#fff',
    border: '2px solid var(--green-eucalyptus, #2ecc40)',
    fontWeight: 'bold',
    color: 'var(--green-eucalyptus, #2ecc40)',
    boxShadow: '0 1px 6px 0 rgba(46,204,64,0.08)',
  },
  arrow: {
    fontSize: 14,
    verticalAlign: 'middle',
  },
};

export default ProjectsPage;